import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { TranslationService } from './translation.service';

/**
 * Service to handle alert presentations in the application.
 *
 * @example
 * ```typescript
 * constructor(private alertService: AlertService) {}
 *
 * this.alertService.present('This is an alert message.');
 * this.alertService.fullAlert('Header', 'Subheader', 'This is a full alert message.');
 * this.alertService.uncaughtErrorAlert('This is an uncaught error.');
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class AlertService {
  translations = {
    uncaught_error_header: '',
    please_report: '',
    upgrade_title: '',
    upgrade_message: '',
    upgrade_button: '',
    new_app_url_android: '',
    new_app_url_ios: '',
    new_app_url_pwa: '',
  };

  constructor(
    private alertController: AlertController,
    private translationService: TranslationService
  ) {
    this.translationService.loadTranslation('error', this.translations);
  }

  /**
   * Presents an alert with the given message.
   *
   * @param message - The message to display in the alert.
   * @returns A promise that resolves when the alert is dismissed.
   */
  public async present(message: string) {
    const alert = await this.alertController.create({
      message: message,
      buttons: ['OK'],
    });

    await alert.present();
    return alert.onDidDismiss();
  }

  /**
   * Presents an alert with the given message and header.
   *
   * @param header - The header to display in the alert.
   * @param subheader - The subheader to display in the alert.
   * @param message - The message to display in the alert.
   * @returns A promise that resolves when the alert is dismissed.
   */
  public async fullAlert(header: string, subheader: string, message: string) {
    const alert = await this.alertController.create({
      header: header,
      subHeader: subheader,
      message: message,
      buttons: ['OK'],
    });

    await alert.present();
    return alert.onDidDismiss();
  }

  /**
   * Presents an alert for an uncaught error.
   *
   * @param error - The error to display in the alert. Can be a string or an Error object.
   * @returns A promise that resolves when the alert is dismissed.
   */
  public async uncaughtErrorAlert(error: string | Error) {
    let message = this.translations.please_report + ' ';
    message += typeof error === 'string' ? error : error.message;
    const alert = await this.alertController.create({
      header: this.translations.uncaught_error_header,
      message: message,
      buttons: ['OK'],
    });

    await alert.present();
    return alert.onDidDismiss();
  }

  /**
   * Presents an upgrade alert that redirects to the new app.
   *
   * @returns A promise that resolves when the alert is dismissed.
   */
  public async upgradeAlert() {
    const alert = await this.alertController.create({
      header: this.translations.upgrade_title,
      message: this.translations.upgrade_message,
      buttons: [
        {
          text: 'OK',
          role: 'cancel',
        },
        {
          text: this.translations.upgrade_button,
          handler: () => {
            void this.openUpgradeLink();
          },
        },
      ],
    });

    await alert.present();
    return alert.onDidDismiss();
  }

  private async openUpgradeLink() {
    const targetUrl = Capacitor.isNativePlatform()
      ? Capacitor.getPlatform() === 'ios'
        ? this.translations.new_app_url_ios
        : this.translations.new_app_url_android
      : this.translations.new_app_url_pwa;

    if (!targetUrl) return;

    await Browser.open({
      url: targetUrl,
      presentationStyle: 'fullscreen',
    });
  }
}
